// src/pages/ExpenseRecord/components/exportExpenseRecordCsv.js
import commaNumber from "comma-number";
import moment from "moment";

const format = commaNumber.bindWith(",", ".");
const fmtNum = (val) => format(parseFloat(val || 0).toFixed(2));
const fmtDate = (date) => (date ? moment(date).format("YYYY-MM-DD") : "");

const escape = (val) => {
  const str = String(val ?? "");
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

/**
 * @param {object} params
 * @param {object}  params.filters              - from Redux state.expenseRecord.filters
 * @param {Array}   params.details              - for "By Expense" mode
 * @param {Array}   params.filterResult         - for "without details" mode
 * @param {Array}   params.filterResultDetails  - for "with details" mode
 * @param {string}  params.title
 * @param {string}  params.filename
 */
export const exportExpenseRecordCsv = ({
  filters,
  details,
  filterResult,
  filterResultDetails,
  title    = "Expense Record",
  filename = "expense_record",
}) => {
  const selectedFilterType  = filters?.selectedFilterType?.filterType;
  const selectedRecordType  = filters?.selectedRecordType?.recordType;

  let headers = [];
  let dataRows = [];
  let grandTotal = 0;

  // ── Mode 1: By Expense ───────────────────────────────────────
  if (selectedFilterType === "By Expense") {
    headers = [
      "No.", "VCH No", "Date", "Payment For",
      "Payment By", "Description", "Total Amount",
    ];

    dataRows = details.map((row, i) => [
      i + 1,
      row.exp_code     || "",
      fmtDate(row.created_date),
      row.acc_name     || "",
      row.full_name    || "",
      row.narration    || "",
      fmtNum(row.exp_amount),
    ]);

    grandTotal = details.reduce((s, r) => s + parseFloat(r.exp_amount || 0), 0);

  // ── Mode 2: With Details ─────────────────────────────────────
  } else if (selectedRecordType === "with details") {
    headers = [
      "No.", "VCH No", "Date", "Payment From",
      "Payment By", "Payment For", "Description", "Payment Amount",
    ];

    let voucherNo = 1;
    filterResultDetails.forEach((voucher) => {
      let subtotal = 0;
      (voucher.details || []).forEach((d, di) => {
        const amt = parseFloat(d.exp_amount || 0);
        subtotal += amt;
        grandTotal += amt;
        dataRows.push([
          di === 0 ? voucherNo : "",   // No. only on first detail row
          di === 0 ? (voucher.exp_code || "") : "",
          di === 0 ? fmtDate(voucher.created_date) : "",
          di === 0 ? (voucher.acc_name  || "") : "",
          di === 0 ? (voucher.full_name || "") : "",
          d.acc_name   || "",
          di === 0 ? (voucher.narration || "") : "",
          fmtNum(d.exp_amount),
        ]);
      });
      // sub-total row
      dataRows.push(["", "", "", "", "", "", "Sub Total:", fmtNum(subtotal)]);
      dataRows.push(Array(8).fill(""));  // blank spacer between vouchers
      voucherNo++;
    });

  // ── Mode 3: Without Details (default) ────────────────────────
  } else {
    headers = [
      "No.", "VCH No", "Date", "Payment From",
      "Pay To", "Payment By", "Description", "Total Amount",
    ];

    dataRows = filterResult.map((row, i) => [
      i + 1,
      row.exp_code  || "",
      fmtDate(row.created_date),
      row.acc_name  || "",
      row.pay_to    || "",
      row.full_name || "",
      row.narration || "",
      fmtNum(row.exp_total),
    ]);

    grandTotal = filterResult.reduce((s, r) => s + parseFloat(r.exp_total || 0), 0);
  }

  const totalRow = [
    "Grand Total:",
    ...Array(headers.length - 2).fill(""),
    fmtNum(grandTotal),
  ];

  const rows = [
    [title],
    [],
    headers.map(escape),
    ...dataRows.map((r) => r.map(escape)),
    [],
    totalRow.map(escape),
  ];

  const csv  = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};